import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useMatch, useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaMapMarkerAlt, FaTag, FaQuestionCircle, FaRocket, FaClipboardList, FaCheckCircle, FaTimesCircle, FaStar } from 'react-icons/fa';
import { Calendar as DatePickerCalendar } from 'react-multi-date-picker';
import DateObject from 'react-date-object';
import { Calendar as CalendarIcon, CalendarDays, MapPin as LucideMapPin, Package, ShieldCheck, Truck as LucideTruck, UserRound, Weight as LucideWeight } from 'lucide-react';
import {
  getPublicationById,
  registerPublicationVisit,
  listSimilarPublications,
} from '../../services/publicationsApi';
import { getCompanyById } from '../../services/companyApi';
import { getUnitById, UNIT_STATUS_OPTIONS } from '../../services/unitsApi';
import { createReservation, RESERVATION_FIELD_LABELS } from '../../services/reservationsApi';
import { listServiceReviews } from '../../services/reviewsApi';
import './PublicationDetail.css';
import { ensureRichText } from '../../utils/richText';
import { getPublicationTypeLabel, isProductPublication, isServicePublication } from '../../constants/publicationTypes';
import { getCurrentAuthUser, onAuthStateChanged } from '../../firebaseAuth';
import ReviewsShowcase from '../../components/ReviewsShowcase';

const POCKETBASE_ID_PATTERN = /^[a-zA-Z0-9]{15,}$/;

const VISIT_STORAGE_KEY = 'flotte:publicationVisitTimestamps';
const VISIT_COOLDOWN_MS = 10 * 60 * 1000;

const AVAILABILITY_FORMAT = 'YYYY-MM-DD';

const UNIT_STATUS_LABELS = UNIT_STATUS_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const stripHtml = value => {
  if (!value) return '';
  return String(value)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const EMPTY_RESERVATION_FORM = {
  requestedFrom: '',
  requestedTo: '',
  pickupLocation: '',
  dropoffLocation: '',
  cargoDescription: '',
  specialRequirements: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  notes: '',
};

const FALLBACK_SIMILAR_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
    <defs>
      <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#1d4ed8" stop-opacity="0.18" />
        <stop offset="100%" stop-color="#3b82f6" stop-opacity="0.32" />
      </linearGradient>
    </defs>
    <rect width="400" height="300" fill="url(#grad)" />
    <g fill="#1e40af" opacity="0.6">
      <circle cx="320" cy="70" r="46" />
      <circle cx="120" cy="220" r="64" />
    </g>
    <rect x="80" y="90" width="240" height="140" rx="20" ry="20" fill="none" stroke="#1e3a8a" stroke-width="8" stroke-dasharray="18 16" opacity="0.7" />
    <path d="M120 210 L180 150 L220 190 L260 150 L300 190" stroke="#2563eb" stroke-width="12" stroke-linecap="round" stroke-linejoin="round" fill="none" opacity="0.75" />
    <circle cx="150" cy="140" r="28" fill="#1d4ed8" opacity="0.75" />
    <text x="200" y="260" font-family="Arial, sans-serif" font-size="24" text-anchor="middle" fill="#0f172a" opacity="0.78">
      Flotte
    </text>
  </svg>`
)}`;

const MAX_SIMILAR_VISIBLE = 8;
const MIN_SIMILAR_COLUMNS = 4;

const DESTINATION_CATALOG = [
  { code: 'MXGTO', label: 'Guanajuato, GTO' },
  { code: 'MXSLP', label: 'San Luis Potosi, SLP' },
  { code: 'MXQRO', label: 'Queretaro, QRO' },
  { code: 'MXMTY', label: 'Monterrey, NL' },
  { code: 'MXMEX', label: 'Ciudad de Mexico, CDMX' },
  { code: 'MXGDL', label: 'Guadalajara, JAL' },
  { code: 'MXTLC', label: 'Toluca, MEX' },
  { code: 'MXVER', label: 'Veracruz, VER' },
  { code: 'MXTIJ', label: 'Tijuana, BCN' },
  { code: 'MXMID', label: 'Merida, YUC' },
];

const getDestinationDisplay = code => {
  if (!code) return '';
  const match = DESTINATION_CATALOG.find(option => option.code === code);
  if (match) {
    return match.label;
  }
  return code;
};

const formatDestinationPrice = (value, currency = 'MXN') => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return 'Consulta precio';
  }
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency || 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency || 'MXN'} ${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
};

const formatDateKey = date => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = value => {
  if (!value) return 'Por definir';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Por definir';
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return value;
  }
};

const formatPrice = value => {
  if (value === null || value === undefined || value === '') {
    return 'Consulta precio';
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric.toLocaleString('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
  return value;
};

const parseDateKey = value => {
  if (!value) return null;
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value === 'string') {
    const clean = value.trim().slice(0, 10);
    const parts = clean.split('-').map(Number);
    if (
      parts.length === 3 &&
      parts.every(item => Number.isInteger(item)) &&
      parts[1] >= 1 &&
      parts[1] <= 12 &&
      parts[2] >= 1 &&
      parts[2] <= 31
    ) {
      const [year, month, day] = parts;
      return new Date(year, month - 1, day);
    }
  }
  return null;
};

const PublicationDetailSkeleton = () => {
  const metaPlaceholders = Array.from({ length: 4 });
  const specPlaceholders = Array.from({ length: 6 });
  const similarPlaceholders = Array.from({ length: 3 });
  const skeletonRelatedLocation = 'tu zona';

  return (
    <div className="publication-detail publication-detail--loading" aria-busy="true">
      <div className="publication-detail__inner">
        <nav className="publication-skeleton__breadcrumbs" aria-hidden="true">
          <span className="publication-skeleton__crumb skeleton" />
          <span className="publication-skeleton__crumb skeleton" />
          <span className="publication-skeleton__crumb publication-skeleton__crumb--wide skeleton" />
        </nav>

        <div className="publication-skeleton__hero">
          <section className="publication-skeleton__hero-main" aria-hidden="true">
            <div className="publication-skeleton__hero-media skeleton" />
            <div className="publication-skeleton__hero-body">
              <span className="publication-skeleton__badge skeleton" />
              <span className="publication-skeleton__title skeleton" />
              <span className="publication-skeleton__subtitle skeleton" />
              <div className="publication-skeleton__hero-row">
                <span className="publication-skeleton__pill skeleton" />
                <span className="publication-skeleton__pill publication-skeleton__pill--short skeleton" />
                <span className="publication-skeleton__pill publication-skeleton__pill--medium skeleton" />
              </div>
              <div className="publication-skeleton__meta">
                {metaPlaceholders.map((_, index) => (
                  <div key={`meta-${index}`} className="publication-skeleton__meta-item">
                    <span className="publication-skeleton__meta-label skeleton" />
                    <span className="publication-skeleton__meta-value skeleton" />
                  </div>
                ))}
              </div>
              <div className="publication-skeleton__actions">
                <span className="publication-skeleton__button skeleton" />
                <span className="publication-skeleton__button publication-skeleton__button--secondary skeleton" />
              </div>
            </div>
          </section>

          <aside className="publication-skeleton__hero-aside" aria-hidden="true">
            <div className="publication-skeleton__card">
              <div className="publication-skeleton__card-media skeleton" />
              <div className="publication-skeleton__stack">
                <span className="publication-skeleton__heading skeleton" />
                <span className="publication-skeleton__line skeleton" />
                <span className="publication-skeleton__line publication-skeleton__line--short skeleton" />
                <div className="publication-skeleton__mini-grid">
                  <span className="publication-skeleton__mini-line skeleton" />
                  <span className="publication-skeleton__mini-line skeleton" />
                </div>
              </div>
              <span className="publication-skeleton__link skeleton" />
            </div>
          </aside>
        </div>

        <div className="publication-skeleton__body">
          <main className="publication-skeleton__main" aria-hidden="true">
            <section className="publication-skeleton__card">
              <div className="publication-skeleton__stack">
                <span className="publication-skeleton__heading publication-skeleton__heading--lg skeleton" />
                <span className="publication-skeleton__line skeleton" />
                <span className="publication-skeleton__line skeleton" />
                <span className="publication-skeleton__line publication-skeleton__line--short skeleton" />
                <span className="publication-skeleton__paragraph skeleton" />
              </div>
            </section>

            <section
              className="publication-card publication-related"
              aria-labelledby="publication-related-title"
            >
              <header className="publication-card__header publication-related__header">
                <span className="icon">
                  <FaRocket aria-hidden="true" />
                </span>
                <div>
                  <h2 id="publication-related-title">Otras opciones en la zona</h2>
                  <p>Publicaciones activas en {skeletonRelatedLocation} con unidades similares.</p>
                </div>
              </header>
              <div className="publication-related__grid">
                {similarPlaceholders.map((_, index) => (
                  <article
                    key={`publication-related-skeleton-${index}`}
                    className="publication-related__item publication-related__item--loading"
                  >
                    <span className="skeleton publication-related__skeleton-media" />
                    <span className="skeleton publication-related__skeleton-badge" />
                    <span className="skeleton publication-related__skeleton-meta" />
                    <span className="skeleton publication-related__skeleton-meta publication-related__skeleton-meta--short" />
                  </article>
                ))}
              </div>
            </section>

            <section className="publication-skeleton__card">
              <div className="publication-skeleton__grid">
                {specPlaceholders.map((_, index) => (
                  <span key={`chip-${index}`} className="publication-skeleton__chip skeleton" />
                ))}
              </div>
            </section>
          </main>

          <aside className="publication-skeleton__aside" aria-hidden="true">
            <section className="publication-skeleton__card">
              <div className="publication-skeleton__stack">
                <span className="publication-skeleton__heading skeleton" />
                <span className="publication-skeleton__line skeleton" />
                <span className="publication-skeleton__line publication-skeleton__line--short skeleton" />
              </div>
              <span className="publication-skeleton__map skeleton" />
            </section>
            <section className="publication-skeleton__card">
              <div className="publication-skeleton__stack">
                <span className="publication-skeleton__heading skeleton" />
                <span className="publication-skeleton__line skeleton" />
                <span className="publication-skeleton__line skeleton" />
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default function PublicationDetail() {
  const { id: identifier } = useParams();
  const navigate = useNavigate();
  const reservationMatch = useMatch('/publicaciones/:id/reservar');
  const isReservationRoute = Boolean(reservationMatch);
  const [publication, setPublication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const [isSlaExpanded, setIsSlaExpanded] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarPopoverRef = useRef(null);
  const dateTriggerRef = useRef(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [companyReviews, setCompanyReviews] = useState([]);
  const [companyReviewsLoading, setCompanyReviewsLoading] = useState(false);
  const [unitAvailabilityRanges, setUnitAvailabilityRanges] = useState([]);
  const [unitDetails, setUnitDetails] = useState(null);
  const [reservationForm, setReservationForm] = useState({ ...EMPTY_RESERVATION_FORM });
  const [reservationSubmitting, setReservationSubmitting] = useState(false);
  const [reservationSuccess, setReservationSuccess] = useState(false);
  const [reservationReceipt, setReservationReceipt] = useState(null);
  const [reservationError, setReservationError] = useState('');
  const [reservationFieldErrors, setReservationFieldErrors] = useState([]);
  const [loadedIdentifier, setLoadedIdentifier] = useState(null);
  const [companyProfileLoading, setCompanyProfileLoading] = useState(false);
  const [similarPublications, setSimilarPublications] = useState([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarError, setSimilarError] = useState('');

  useEffect(() => {
    if (isReservationRoute) {
      setReservationError('');
      setReservationFieldErrors([]);
      if (typeof document !== 'undefined') {
        const reservationSection = document.getElementById('publication-reservation');
        if (reservationSection) {
          reservationSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } else {
      setReservationSuccess(false);
      setReservationForm({ ...EMPTY_RESERVATION_FORM });
      setReservationFieldErrors([]);
      setReservationReceipt(null);
    }
  }, [isReservationRoute]);


  useEffect(() => {
    if (!publication) {
      setUnitAvailabilityRanges([]);
      setUnitDetails(null);
      return;
    }
    const snapshot = Array.isArray(publication.unitAvailabilityRanges)
      ? publication.unitAvailabilityRanges
      : [];
    const hasUnitReference = Boolean(
      publication.unitId || publication.unit || publication.unitRef || publication.unitReference
    );
    if (snapshot.length || !hasUnitReference) {
      setUnitAvailabilityRanges(snapshot);
      if (!hasUnitReference) {
        setUnitDetails(null);
      }
    }
  }, [publication]);

  useEffect(() => {
    setIsSlaExpanded(true);
  }, [publication?.id]);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      if (!identifier) {
        setError('No encontramos la publicacion solicitada.');
        setLoading(false);
        setPublication(null);
        setCompanyProfile(null);
        setUnitDetails(null);
        setUnitAvailabilityRanges([]);
        setLoadedIdentifier(null);
        setCompanyProfileLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      setPublication(null);
      setCompanyProfile(null);
      setUnitDetails(null);
      setUnitAvailabilityRanges([]);

      try {
        const record = await getPublicationById(identifier);
        if (!ignore) {
          setCompanyProfileLoading(Boolean(record?.company));
          setPublication(record);
          setLoadedIdentifier(identifier);
        }
      } catch (err) {
        if (!ignore) {
          setError(err?.message || 'No fue posible obtener la publicacion.');
          setCompanyProfileLoading(false);
          setLoadedIdentifier(identifier);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [identifier]);

  const companyId = publication?.company;
  const publicationId = publication?.id || '';
  const [authUser, setAuthUser] = useState(() => getCurrentAuthUser());
  useEffect(() => onAuthStateChanged(setAuthUser), []);
  const currentUser = authUser || null;
  const currentUserId = currentUser?.id ? String(currentUser.id) : '';
  const currentUserRole = String(currentUser?.role || '').toLowerCase();
  const userContactDefaults = useMemo(() => {
    const normalize = value => {
      if (typeof value === 'string') {
        return value.trim();
      }
      return '';
    };

    const combinedName = normalize(
      [normalize(currentUser?.firstName), normalize(currentUser?.lastName)].filter(Boolean).join(' ')
    );

    return {
      name:
        normalize(currentUser?.name) ||
        normalize(currentUser?.fullName) ||
        normalize(currentUser?.displayName) ||
        combinedName ||
        normalize(currentUser?.firstName) ||
        normalize(currentUser?.username),
      email:
        normalize(currentUser?.email) ||
        normalize(currentUser?.contactEmail) ||
        normalize(currentUser?.correo),
      phone:
        normalize(currentUser?.phoneNumber) ||
        normalize(currentUser?.phone) ||
        normalize(currentUser?.telefono) ||
        normalize(currentUser?.celular) ||
        normalize(currentUser?.mobile) ||
        normalize(currentUser?.contactPhone),
    };
  }, [
    currentUser?.name,
    currentUser?.fullName,
    currentUser?.displayName,
    currentUser?.firstName,
    currentUser?.lastName,
    currentUser?.username,
    currentUser?.email,
    currentUser?.contactEmail,
    currentUser?.correo,
    currentUser?.phoneNumber,
    currentUser?.phone,
    currentUser?.telefono,
    currentUser?.celular,
    currentUser?.mobile,
    currentUser?.contactPhone,
  ]);
  const userCompanyIds = useMemo(() => {
    const ids = [];
    const pushId = value => {
      if (!value) return;
      const normalized = String(value).trim();
      if (normalized && !ids.includes(normalized)) {
        ids.push(normalized);
      }
    };
    pushId(currentUser?.company);
    pushId(currentUser?.companyId);
    if (Array.isArray(currentUser?.companies)) {
      currentUser.companies.forEach(pushId);
    } else if (currentUser?.companies && typeof currentUser.companies === 'string') {
      pushId(currentUser.companies);
    }
    return ids;
  }, [currentUser?.company, currentUser?.companyId, currentUser?.companies]);
  const publicationCompanyCandidates = useMemo(() => {
    return [
      publication?.companyId,
      publication?.company,
      companyProfile?.id,
    ]
      .map(value => (value ? String(value).trim() : ''))
      .filter(Boolean);
  }, [publication?.companyId, publication?.company, companyProfile?.id]);
  const reviewTargetCompanyId = useMemo(
    () => publicationCompanyCandidates[0] || '',
    [publicationCompanyCandidates]
  );
  const publicationCreatorId = publication?.createdBy
    ? String(publication.createdBy).trim()
    : '';
  const isOwnPublication = useMemo(() => {
    if (!currentUserId && userCompanyIds.length === 0) {
      return false;
    }
    if (currentUserId && publicationCreatorId && publicationCreatorId === currentUserId) {
      return true;
    }
    return publicationCompanyCandidates.some(candidate => userCompanyIds.includes(candidate));
  }, [currentUserId, publicationCreatorId, publicationCompanyCandidates, userCompanyIds]);
  const isClientSelfReservation = currentUserRole === 'client' && isOwnPublication;

  useEffect(() => {
    if (!isReservationRoute) return;
    if (!currentUser) return;
    if (!userContactDefaults.name && !userContactDefaults.email && !userContactDefaults.phone) {
      return;
    }

    setReservationForm(prev => {
      let changed = false;
      const patch = {};

      if (!prev.contactName && userContactDefaults.name) {
        patch.contactName = userContactDefaults.name;
        changed = true;
      }
      if (!prev.contactEmail && userContactDefaults.email) {
        patch.contactEmail = userContactDefaults.email;
        changed = true;
      }
      if (!prev.contactPhone && userContactDefaults.phone) {
        patch.contactPhone = userContactDefaults.phone;
        changed = true;
      }

      return changed ? { ...prev, ...patch } : prev;
    });
  }, [
    isReservationRoute,
    currentUser,
    userContactDefaults.name,
    userContactDefaults.email,
    userContactDefaults.phone,
  ]);

  useEffect(() => {
    let ignore = false;

    const loadCompanyProfile = async () => {
      if (!companyId) {
        if (!ignore) {
          setCompanyProfile(null);
          setCompanyProfileLoading(false);
        }
        return;
      }

      if (!ignore) {
        setCompanyProfileLoading(true);
      }
      try {
        const record = await getCompanyById(companyId);
        if (!ignore) {
          setCompanyProfile(record);
        }
      } catch (err) {
        if (!ignore) {
          console.error('No se pudo obtener la empresa asociada a la publicacion.', err);
          setCompanyProfile(null);
        }
      } finally {
        if (!ignore) {
          setCompanyProfileLoading(false);
        }
      }
    };

    loadCompanyProfile();
    return () => {
      ignore = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!reviewTargetCompanyId) {
      setCompanyReviews([]);
      return;
    }
    let ignore = false;
    setCompanyReviewsLoading(true);
    listServiceReviews({
      targetCompanyId: reviewTargetCompanyId,
      direction: 'client_to_provider',
      limit: 6,
    })
      .then(response => {
        if (!ignore) {
          setCompanyReviews(response?.items || []);
        }
      })
      .catch(() => {
        if (!ignore) {
          setCompanyReviews([]);
        }
      })
      .finally(() => {
        if (!ignore) {
          setCompanyReviewsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [reviewTargetCompanyId]);

  useEffect(() => {
    if (!publicationId) return;
    if (typeof window === 'undefined') return;

    let cache = {};
    try {
      const stored = window.localStorage.getItem(VISIT_STORAGE_KEY);
      cache = stored ? JSON.parse(stored) || {} : {};
    } catch {
      cache = {};
    }

    const now = Date.now();
    const lastVisit = Number(cache[publicationId] || 0);
    const withinCooldown =
      Number.isFinite(lastVisit) && now - lastVisit < VISIT_COOLDOWN_MS;
    if (withinCooldown) return;

    cache[publicationId] = now;
    try {
      window.localStorage.setItem(VISIT_STORAGE_KEY, JSON.stringify(cache));
    } catch {
      // ignore storage write errors
    }

    const authModel = authUser || null;
    const viewerId = authModel?.id || '';
    const viewerLabel =
      authModel?.name ||
      authModel?.username ||
      authModel?.email ||
      (viewerId ? 'Miembro de Flotte' : '');
    const viewerUsername = authModel?.username || '';

    registerPublicationVisit({
      id: publicationId,
      viewerId,
      viewerLabel,
      viewerUsername,
    })
      .then(updated => {
        if (updated && typeof updated.views === 'number') {
          setPublication(prev => (prev ? { ...prev, views: updated.views } : prev));
        }
      })
      .catch(error => {
        console.warn('No se pudo registrar la visita de la publicacion.', error);
      });
  }, [authUser, publicationId]);

  useEffect(() => {
    if (!publication) {
      setSimilarPublications([]);
      setSimilarError('');
      setSimilarLoading(false);
      return;
    }

    const locationCandidate = String(publication.location || '').trim();
    const typeCandidate = publication.type || '';
    const unitNameCandidate = String(publication.unitName || '').trim();

    if (!locationCandidate && !typeCandidate && !unitNameCandidate) {
      setSimilarPublications([]);
      setSimilarError('');
      setSimilarLoading(false);
      return;
    }

    let ignore = false;
    const loadSimilar = async () => {
      setSimilarLoading(true);
      setSimilarError('');
      try {
        const items = await listSimilarPublications({
          location: locationCandidate,
          type: typeCandidate,
          unitName: unitNameCandidate,
          excludeId: publication.id,
          limit: 4,
        });
        if (!ignore) {
          const filtered = Array.isArray(items)
            ? items.filter(item => item && item.id !== publication.id)
            : [];
          setSimilarPublications(filtered.slice(0, 3));
        }
      } catch (error) {
        if (!ignore) {
          console.warn('No se pudieron cargar publicaciones sugeridas.', error);
          setSimilarPublications([]);
          setSimilarError('No pudimos cargar sugerencias en este momento.');
        }
      } finally {
        if (!ignore) {
          setSimilarLoading(false);
        }
      }
    };

    loadSimilar();
    return () => {
      ignore = true;
    };
  }, [publication]);

  const publicationType = publication?.type;
  const isServiceOffer = useMemo(() => isServicePublication(publicationType), [publicationType]);
  const isProductOffer = useMemo(() => isProductPublication(publicationType), [publicationType]);

  const availabilityRange = useMemo(() => {
    if (!publication) {
      return {
        start: null,
        end: null,
        value: [],
      };
    }

    const startDate = publication.availableFrom ? new Date(publication.availableFrom) : null;
    const endDate = publication.availableTo ? new Date(publication.availableTo) : null;

    const isValidDate = date => date instanceof Date && !Number.isNaN(date.getTime());

    if (isValidDate(startDate) && isValidDate(endDate) && startDate <= endDate) {
      return {
        start: startDate,
        end: endDate,
        value: [new DateObject(startDate), new DateObject(endDate)],
      };
    }

    return {
      start: null,
      end: null,
      value: [],
    };
  }, [publication]);


  const normalizeUnitId = useCallback(value => {
    if (!value) return '';
    const resolveString = candidate => {
      const normalized = typeof candidate === 'string' || typeof candidate === 'number'
        ? String(candidate).trim()
        : '';
      return normalized && POCKETBASE_ID_PATTERN.test(normalized) ? normalized : '';
    };

    if (typeof value === 'string' || typeof value === 'number') {
      return resolveString(value);
    }
    if (Array.isArray(value)) {
      return value.map(entry => normalizeUnitId(entry)).find(Boolean) || '';
    }
    if (typeof value === 'object') {
      return (
        normalizeUnitId(value.id) ||
        normalizeUnitId(value.value) ||
        normalizeUnitId(value.unitId) ||
        normalizeUnitId(value.unit) ||
        normalizeUnitId(value.reference)
      );
    }
    return '';
  }, []);

  const primaryUnitId = useMemo(() => {
    if (!publication) return '';
    const candidate =
      normalizeUnitId(publication?.unitId) ||
      normalizeUnitId(publication?.unit) ||
      normalizeUnitId(publication?.unitRef) ||
      normalizeUnitId(publication?.unitReference) ||
      '';
    return candidate;
  }, [publication, normalizeUnitId]);

  const hasUnitAssigned = Boolean(primaryUnitId);
  const unitStatus = hasUnitAssigned ? (unitDetails?.status || 'available') : 'available';
  const hasActiveReservationBlock = useMemo(() => {
    if (!hasUnitAssigned) return false;
    if (!Array.isArray(unitAvailabilityRanges) || !unitAvailabilityRanges.length) return false;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return unitAvailabilityRanges.some(range => {
      if (!range) return false;
      const startDate = parseDateKey(range.start);
      const endCandidate = parseDateKey(range.end) || startDate;
      if (!startDate) return false;
      const normalizedStart = startDate;
      const normalizedEnd =
        endCandidate && endCandidate >= normalizedStart ? endCandidate : normalizedStart;
      return normalizedStart <= today && normalizedEnd >= today;
    });
  }, [hasUnitAssigned, unitAvailabilityRanges]);
  const resolvedUnitStatus =
    hasUnitAssigned && unitStatus === 'reserved' && !hasActiveReservationBlock
      ? 'available'
      : unitStatus;
  const unitStatusLabel = hasUnitAssigned
    ? UNIT_STATUS_LABELS[resolvedUnitStatus] || 'Disponible'
    : 'Se confirma con el proveedor';
  const unitStatusClassName = hasUnitAssigned ? `status-${resolvedUnitStatus}` : 'status-available';
  const publicationStatus = publication?.status || '';
  const reservationBlockedReason = useMemo(() => {
    if (isClientSelfReservation) {
      return 'No puedes reservar una publicacion creada por tu empresa.';
    }
    if (publicationStatus && publicationStatus !== 'published') {
      return 'La oferta no esta publicada en este momento.';
    }
    if (unitStatus === 'maintenance') {
      return 'La unidad esta marcada en mantenimiento.';
    }
    if (unitStatus === 'unavailable') {
      return 'La unidad no esta disponible temporalmente.';
    }
    return '';
  }, [isClientSelfReservation, publicationStatus, unitStatus]);
  const canRequestReservation = !reservationBlockedReason;

  const handleReservationNavigate = useCallback(() => {
    if (!identifier) return;
    navigate(`/publicaciones/${identifier}/reservar`);
  }, [identifier, navigate]);

  const handleReservationClose = useCallback(() => {
    if (!identifier) return;
    navigate(`/publicaciones/${identifier}`);
  }, [identifier, navigate]);

  const handleGoToReservations = useCallback(() => {
    setReservationSuccess(false);
    setReservationReceipt(null);
    navigate('/dashboard?section=reservas');
  }, [navigate]);

  const handleGoHome = useCallback(() => {
    setReservationSuccess(false);
    setReservationReceipt(null);
    navigate('/');
  }, [navigate]);

  const handleReservationFieldChange = useCallback(event => {
    const { name, value } = event.target;
    setReservationForm(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleToggleCalendar = useCallback(() => {
    setShowCalendar(prev => !prev);
  }, []);

  useEffect(() => {
    if (!showCalendar) return undefined;
    const handleClickOutside = event => {
      const popoverEl = calendarPopoverRef.current;
      const triggerEl = dateTriggerRef.current;
      if (
        popoverEl &&
        !popoverEl.contains(event.target) &&
        triggerEl &&
        !triggerEl.contains(event.target)
      ) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showCalendar]);

  const reservationSelectionRange = useMemo(() => {
    const startDate = parseDateKey(reservationForm.requestedFrom);
    if (!startDate) return null;
    const endDate = reservationForm.requestedTo
      ? parseDateKey(reservationForm.requestedTo)
      : startDate;
    if (endDate && endDate < startDate) {
      return null;
    }
    return {
      start: startDate,
      end: endDate || startDate,
    };
  }, [reservationForm.requestedFrom, reservationForm.requestedTo]);

  const reservationDateConflict = useMemo(() => {
    if (!reservationSelectionRange) return null;
    if (!Array.isArray(unitAvailabilityRanges) || !unitAvailabilityRanges.length) return null;
    const selectedStart = reservationSelectionRange.start;
    const selectedEnd = reservationSelectionRange.end;
    const conflictingRange = unitAvailabilityRanges.find(range => {
      if (!range) return false;
      const blockedStart = parseDateKey(range.start);
      const blockedEndCandidate = parseDateKey(range.end) || blockedStart;
      if (!blockedStart) return false;
      const blockedEnd =
        blockedEndCandidate && blockedEndCandidate >= blockedStart
          ? blockedEndCandidate
          : blockedStart;
      return blockedStart <= selectedEnd && blockedEnd >= selectedStart;
    });
    if (!conflictingRange) return null;
    return {
      message: 'La unidad no esta disponible en esas fechas.',
      range: conflictingRange,
    };
  }, [reservationSelectionRange, unitAvailabilityRanges]);

  const handleReservationSubmit = useCallback(
    async event => {
      event.preventDefault();
      if (reservationSubmitting) return;
      setReservationFieldErrors([]);
      if (!publication) {
        setReservationError('No pudimos identificar la publicacion a reservar.');
        return;
      }

      const requiredFields = [
        { key: 'requestedFrom', message: 'Selecciona cuando necesitas el servicio.' },
        { key: 'pickupLocation', message: 'Indica el punto de recoleccion.' },
        { key: 'dropoffLocation', message: 'Indica el destino de entrega.' },
        { key: 'cargoDescription', message: 'Describe tu mercancia.' },
        { key: 'contactName', message: 'Captura el nombre de la persona de contacto.' },
        { key: 'contactEmail', message: 'Captura el correo de contacto.' },
        { key: 'contactPhone', message: 'Captura el telefono de contacto.' },
      ];

      for (const field of requiredFields) {
        const value = reservationForm[field.key];
        if (!value || !String(value).trim()) {
          setReservationError(field.message);
          const label =
            RESERVATION_FIELD_LABELS[field.key] ||
            field.message ||
            field.key;
          setReservationFieldErrors([
            {
              field: field.key,
              label,
              message: field.message,
            },
          ]);
          return;
        }
      }

      if (
        reservationForm.requestedTo &&
        reservationForm.requestedTo < reservationForm.requestedFrom
      ) {
        setReservationError('La fecha de termino no puede ser anterior a la fecha de inicio.');
        setReservationFieldErrors([
          {
            field: 'requestedTo',
            label: RESERVATION_FIELD_LABELS.requestedTo || 'Fecha de termino',
            message: 'La fecha de termino no puede ser anterior a la fecha de inicio.',
          },
        ]);
        return;
      }

      if (reservationDateConflict) {
        const conflictMessage =
          reservationDateConflict.message || 'La unidad no esta disponible en esas fechas.';
        setReservationError(conflictMessage);
        setReservationFieldErrors([
          {
            field: 'requestedFrom',
            label: RESERVATION_FIELD_LABELS.requestedFrom || 'Fecha de inicio',
            message: conflictMessage,
          },
        ]);
        return;
      }

      if (!canRequestReservation) {
        setReservationError(reservationBlockedReason || 'Esta publicacion no puede recibir reservas ahora.');
        setReservationFieldErrors([]);
        return;
      }

      setReservationSubmitting(true);
      setReservationError('');
      setReservationFieldErrors([]);

      try {
        const payload = {
          status: 'pending',
          publicationId: publication.id,
          companyId: publication.companyId || companyProfile?.id || '',
          requestedFrom: reservationForm.requestedFrom,
          requestedTo: reservationForm.requestedTo,
          pickupLocation: reservationForm.pickupLocation,
          dropoffLocation: reservationForm.dropoffLocation,
          cargoDescription: reservationForm.cargoDescription,
          specialRequirements: reservationForm.specialRequirements,
          contactName: reservationForm.contactName,
          contactEmail: reservationForm.contactEmail,
          contactPhone: reservationForm.contactPhone,
          notes: reservationForm.notes,
        };

        const resolvedUnitId =
          unitDetails?.id ||
          (primaryUnitId && POCKETBASE_ID_PATTERN.test(primaryUnitId) ? primaryUnitId : '');

        if (resolvedUnitId) {
          payload.unitId = resolvedUnitId;
        }

        await createReservation(payload);
        setReservationSuccess(true);
        setReservationReceipt({
          pickupLocation: reservationForm.pickupLocation,
          dropoffLocation: reservationForm.dropoffLocation,
          requestedFrom: reservationForm.requestedFrom,
          requestedTo: reservationForm.requestedTo,
          serviceLabel: getPublicationTypeLabel(publicationType),
        });
        setReservationForm({ ...EMPTY_RESERVATION_FORM });
        setReservationFieldErrors([]);
      } catch (submitError) {
        console.error('No se pudo registrar la reserva.', submitError);
        setReservationError(
          submitError?.message ||
            'No pudimos registrar tu reserva. Intenta nuevamente en unos minutos.'
        );
        setReservationFieldErrors(
          Array.isArray(submitError?.fieldErrors) ? submitError.fieldErrors : []
        );
      } finally {
        setReservationSubmitting(false);
      }
    },
    [
      reservationSubmitting,
      publication,
      primaryUnitId,
      unitDetails?.id,
      reservationForm,
      canRequestReservation,
      reservationBlockedReason,
      companyProfile,
      reservationDateConflict,
      publicationType,
    ]
  );

  useEffect(() => {
    let ignore = false;

    const loadUnitAvailability = async () => {
      const snapshot = Array.isArray(publication?.unitAvailabilityRanges)
        ? publication.unitAvailabilityRanges
        : [];

      if (!primaryUnitId) {
        if (!ignore) {
          setUnitAvailabilityRanges(snapshot);
          setUnitDetails(null);
        }
        return;
      }
      try {
        const unit = await getUnitById(primaryUnitId);
        if (!ignore) {
          setUnitDetails(unit);
          const ranges = Array.isArray(unit?.availabilityRanges)
            ? unit.availabilityRanges
            : [];
          setUnitAvailabilityRanges(ranges.length ? ranges : snapshot);
        }
      } catch (err) {
        console.error('No se pudo obtener la disponibilidad de la unidad.', err);
        if (!ignore) {
          setUnitAvailabilityRanges(prev => {
            if (Array.isArray(prev) && prev.length) {
              return prev;
            }
            return snapshot;
          });
          setUnitDetails(prev => prev || null);
        }
      }
    };

    loadUnitAvailability();
    return () => {
      ignore = true;
    };
  }, [primaryUnitId, publication]);

  const availabilityCalendar = useMemo(() => {
    const dayKeys = new Set();
    const monthKeys = new Set();
    const startKeys = new Set();
    const endKeys = new Set();
    let minDate = null;
    let maxDate = null;

    const pushDate = date => {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
      const normalized = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const key = formatDateKey(normalized);
      if (!key) return;
      dayKeys.add(key);
      monthKeys.add(key.slice(0, 7));
      if (!minDate || normalized < minDate) {
        minDate = normalized;
      }
      if (!maxDate || normalized > maxDate) {
        maxDate = normalized;
      }
    };

    if (Array.isArray(unitAvailabilityRanges) && unitAvailabilityRanges.length) {
      unitAvailabilityRanges.forEach(range => {
        if (!range) return;
        const startCandidate = parseDateKey(range.start);
        const endCandidate = parseDateKey(range.end);
        if (!startCandidate) return;
        const startDate =
          endCandidate && endCandidate < startCandidate ? endCandidate : startCandidate;
        const endDate =
          endCandidate && endCandidate > startCandidate ? endCandidate : startCandidate;

        if (endDate > startDate) {
          startKeys.add(formatDateKey(startDate));
          endKeys.add(formatDateKey(endDate));
          let cursor = startDate;
          while (cursor <= endDate) {
            pushDate(cursor);
            cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
          }
        } else {
          pushDate(startDate);
          const key = formatDateKey(startDate);
          if (key) {
            startKeys.add(key);
            endKeys.add(key);
          }
        }
      });
    }

    return {
      dates: dayKeys,
      months: monthKeys.size || 1,
      hasUnitRanges: dayKeys.size > 0,
      firstDate: minDate,
      lastDate: maxDate,
      startDates: startKeys,
      endDates: endKeys,
    };
  }, [unitAvailabilityRanges]);

  const availabilityDates = availabilityCalendar.dates;
  const showUnitAvailability = availabilityCalendar.hasUnitRanges;
  const availabilityRangeStarts = availabilityCalendar.startDates;
  const availabilityRangeEnds = availabilityCalendar.endDates;
  const publicationRangeStart = availabilityRange.start;
  const publicationRangeEnd = availabilityRange.end;
  const firstAvailableDate = availabilityCalendar.firstDate;
  const lastAvailableDate = availabilityCalendar.lastDate;
  const calendarMonths = 1;
  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);
  const calendarValue = useMemo(() => {
    if (showUnitAvailability) {
      return undefined;
    }
    if (availabilityRange.value.length) {
      return availabilityRange.value;
    }
    return null;
  }, [availabilityRange.value, showUnitAvailability]);
  const publicationRangeStartKey = publicationRangeStart ? formatDateKey(publicationRangeStart) : '';
  const publicationRangeEndKey = publicationRangeEnd ? formatDateKey(publicationRangeEnd) : '';
  const hasPublicationRange = Boolean(publicationRangeStartKey && publicationRangeEndKey);
  const assumeAlwaysAvailable = !showUnitAvailability && !hasPublicationRange && isProductOffer;
  const calendarFooterText = showUnitAvailability
    ? 'Coordina con el proveedor si necesitas una ventana alterna.'
    : hasPublicationRange
    ? 'Esta oferta estara disponible solo durante el periodo indicado.'
    : assumeAlwaysAvailable
    ? 'El proveedor marco esta publicacion como disponible permanentemente.'
    : 'La unidad no cuenta con disponibilidad publicada. Contacta al proveedor para confirmar.';
  const triggerDateFormatter = useCallback(date => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    try {
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
      });
    } catch {
      return '';
    }
  }, []);
  const dateTriggerStartDate = showUnitAvailability ? firstAvailableDate : publicationRangeStart;
  const dateTriggerEndDate = showUnitAvailability ? lastAvailableDate : publicationRangeEnd;
  const dateTriggerStartLabel = triggerDateFormatter(dateTriggerStartDate) || 'Seleccionar';
  const dateTriggerEndLabel = triggerDateFormatter(dateTriggerEndDate) || 'Seleccionar';

  const destinationPricingEntries = useMemo(() => {
    const pricingDestinations = Array.isArray(publication?.pricing?.destinations)
      ? publication.pricing.destinations
      : [];
    const unitDestinations = Array.isArray(publication?.unitDestinationPrices)
      ? publication.unitDestinationPrices
      : [];
    const unitDestinationsFromDetails = Array.isArray(unitDetails?.destinationPrices)
      ? unitDetails.destinationPrices
      : [];
    const fallbackDestinations = Array.isArray(publication?.destinationPrices)
      ? publication.destinationPrices
      : [];
    const source = pricingDestinations.length
      ? pricingDestinations
      : unitDestinations.length
      ? unitDestinations
      : unitDestinationsFromDetails.length
      ? unitDestinationsFromDetails
      : fallbackDestinations;
    if (!source.length) return [];
    const currency =
      publication?.pricing?.currency ||
      publication?.priceCurrency ||
      unitDetails?.currency ||
      'MXN';
    return source
      .map(entry => {
        if (!entry) return null;
        const destinationCodeRaw =
          entry.destinationCode ||
          entry.code ||
          entry.destination ||
          entry.destinationInput ||
          entry.location ||
          '';
        const destinationCode = String(destinationCodeRaw).trim();
        const catalogLabel = getDestinationDisplay(destinationCode);
        const resolvedLabel = [
          entry.destinationLabel,
          entry.label,
          entry.destination,
          entry.location,
          catalogLabel,
          destinationCode,
        ]
          .map(value => (typeof value === 'string' ? value.trim() : ''))
          .find(Boolean) || '';
        if (!resolvedLabel) return null;
        const priceCandidate =
          entry.price ??
          entry.amount ??
          (typeof entry.priceLabel === 'string' && entry.priceLabel.trim()
            ? entry.priceLabel
            : null);
        const priceLabel =
          typeof priceCandidate === 'string'
            ? priceCandidate
            : formatDestinationPrice(priceCandidate, currency);
        const hasQuotedPrice = Boolean(
          typeof priceCandidate === 'string'
            ? priceCandidate && priceCandidate.toLowerCase() !== 'consulta precio'
            : priceCandidate !== null && priceCandidate !== undefined
        );
        return {
          id: entry.id || `${destinationCode || resolvedLabel}-${priceLabel}`,
          destinationCode: destinationCode || '',
          label: resolvedLabel,
          priceLabel: priceLabel || 'Consulta precio',
          hasQuotedPrice,
          isFallback: false,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [publication, unitDetails]);
  const relatedLocationLabel = useMemo(() => {
    if (!publication) return 'tu zona';
    const locationCandidate = String(publication.location || '').trim();
    if (locationCandidate) return locationCandidate;
    const unitNameCandidate = String(publication.unitName || '').trim();
    if (unitNameCandidate) return unitNameCandidate;
    const companyCandidate = String(publication.companyName || '').trim();
    if (companyCandidate) return companyCandidate;
    return 'tu zona';
  }, [publication]);
  const shouldShowSimilarSection =
    similarLoading ||
    (Array.isArray(similarPublications) && similarPublications.length > 0) ||
    Boolean(similarError);

  if (
    loading ||
    (identifier && identifier !== loadedIdentifier) ||
    companyProfileLoading
  ) {
    return <PublicationDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="publication-detail publication-detail--error">
        <div className="publication-detail__inner">
          <div className="publication-detail__feedback">
            <h1>Lo sentimos</h1>
            <p>{error}</p>
            <Link to="/marketplace" className="btn btn-primary">
              Volver al marketplace
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!publication) {
    return (
      <div className="publication-detail publication-detail--error">
        <div className="publication-detail__inner">
          <div className="publication-detail__feedback">
            <h1>Publicacion no encontrada</h1>
            <p>Es posible que haya sido retirada o que el enlace sea incorrecto.</p>
            <Link to="/marketplace" className="btn btn-primary">
              Volver al marketplace
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const priceSource = publication.priceLabel || publication.price;
  const priceLabel = formatPrice(priceSource);
  const baseCompanyLabel = publication.companyName || 'Proveedor aliado';
  const companyName = companyProfile?.name || baseCompanyLabel;
  const companyInitial = companyName?.trim()?.[0]?.toUpperCase() || 'F';
  const fallbackCompanySlug = publication.companySlug || '';
  const fallbackCompanyLogo = publication.companyLogoUrl || '';
  const fallbackCompanyHero = publication.companyHeroUrl || '';
  const companySlug = companyProfile?.slug || fallbackCompanySlug;
  const companyLogoUrl = companyProfile?.logoUrl || fallbackCompanyLogo;
  const companyHeroUrl = companyProfile?.bannerUrl || fallbackCompanyHero;
  const companyProfileHref = companySlug ? `/empresa/${companySlug}` : '/marketplace';
  const isVerifiedCompany = Boolean(
    companyProfile?.isVerified ??
      companyProfile?.verificado ??
      publication?.companyVerified ??
      publication?.isVerified ??
      publication?.company?.isVerified ??
      publication?.company?.verificado
  );
  const hasPolicies = Boolean(publication.policies);
  const hasSlaContent = Boolean(publication.sla && publication.sla.trim());
  const hasFaqs = Array.isArray(publication.faqs) && publication.faqs.length > 0;
  const locationLabel = publication.location || publication.unitName || 'Ubicacion por confirmar';
  const useCompanyHero = publication.bannerPreference === 'company' && companyHeroUrl;
  const useUnitBanner = publication.bannerPreference === 'unit' && publication.bannerUrl;
  const useCustomBanner = publication.bannerPreference === 'custom' && publication.bannerUrl;
  const heroImage = useCustomBanner
    ? publication.bannerUrl
    : useUnitBanner
    ? publication.bannerUrl
    : useCompanyHero
    ? companyHeroUrl
    : '';
  const shouldRenderHero = Boolean(heroImage);
  const heroTitleId = 'publication-hero-title';
  const heroMediaStyle = shouldRenderHero
    ? {
        backgroundImage: `url(${heroImage})`,
      }
    : undefined;
  const baseDestinationEntry = {
    id: 'base-destination',
    destinationCode:
      publication?.pricing?.baseDestinationCode ||
      publication?.primaryDestinationCode ||
      '',
    label: locationLabel || publication?.title || 'Precio referencial',
    priceLabel: priceLabel || unitDetails?.priceLabel || 'Consulta precio',
    hasQuotedPrice: Boolean(
      (priceLabel && priceLabel !== 'Consulta precio') ||
        (unitDetails?.priceLabel && unitDetails.priceLabel !== 'Consulta precio')
    ),
    isFallback: true,
  };
  const destinationSectionEntries = destinationPricingEntries.length
    ? destinationPricingEntries
    : [baseDestinationEntry];
  const hasMultipleDestinations = destinationPricingEntries.length > 0;
  const shouldShowDestinationPricing = destinationSectionEntries.length > 0;
  const sidebarPriceLabel = baseDestinationEntry.priceLabel || priceLabel || 'Consulta precio';
  const specItems = [];
  const resolveSpecValue = candidates => {
    const value = candidates.map(item => (typeof item === 'number' ? String(item) : item)).find(
      entry => typeof entry === 'string' && entry.trim()
    );
    return value ? value.trim() : '';
  };
  const capacityValue = resolveSpecValue([
    publication?.unitCapacity,
    publication?.capacity,
    unitDetails?.capacity,
    publication?.payloadCapacity,
  ]);
  const unitTypeValue = resolveSpecValue([
    publication?.unitTypeLabel,
    publication?.unitType,
    publication?.unitCategory,
    unitDetails?.tractorType,
    publication?.unitName,
  ]);
  const unitYearValue = resolveSpecValue([
    publication?.unitYear,
    publication?.year,
    publication?.modelYear,
    unitDetails?.model,
  ]);
  const baseLocationValue = resolveSpecValue([locationLabel, publication?.baseLocation, publication?.origin]);

  specItems.push({
    key: 'unit-type',
    label: 'Tipo',
    value: unitTypeValue || 'Por confirmar',
    icon: LucideTruck,
    accent: '#4f46e5',
    accentSoft: 'rgba(79, 70, 229, 0.12)',
  });
  specItems.push({
    key: 'capacity',
    label: 'Capacidad',
    value: capacityValue || 'Por confirmar',
    icon: LucideWeight,
    accent: '#0ea5e9',
    accentSoft: 'rgba(14, 165, 233, 0.12)',
  });
  specItems.push({
    key: 'unit-year',
    label: 'Modelo',
    value: unitYearValue || 'Por confirmar',
    icon: CalendarIcon,
    accent: '#0f172a',
    accentSoft: 'rgba(15, 23, 42, 0.12)',
  });
  specItems.push({
    key: 'base-location',
    label: 'Base',
    value: baseLocationValue || 'Por confirmar',
    icon: LucideMapPin,
    accent: '#22c55e',
    accentSoft: 'rgba(34, 197, 94, 0.12)',
  });
  const renderSpecItem = item => {
    if (!item) return null;
    const Icon = item.icon;
    const valueContent = item.renderValue ? item.renderValue() : item.value;

    return (
      <div key={item.key} className="publication-feature" role="listitem">
        <span
          className="publication-feature__icon"
          aria-hidden="true"
          style={
            item.accent
              ? {
                  color: item.accent,
                  backgroundColor: item.accentSoft,
                }
              : undefined
          }
        >
          <Icon aria-hidden="true" size={20} strokeWidth={1.6} />
        </span>
        <div className="publication-feature__text">
          <p className="publication-feature__label">{item.label}</p>
          <p className="publication-feature__value">{valueContent}</p>
          {item.helper ? <small>{item.helper}</small> : null}
        </div>
      </div>
    );
  };

  const showCompanyCard = Boolean(companyName);

  if (isReservationRoute) {
    const reservationFormId = 'publication-reservation-form';
    const requestedRangeLabel = reservationForm.requestedFrom
      ? reservationForm.requestedTo
        ? `${reservationForm.requestedFrom} → ${reservationForm.requestedTo}`
        : reservationForm.requestedFrom
      : 'Por definir';
    const reservationSummaryItems = [
      { label: 'Proveedor', value: companyName },
      { label: 'Ubicación base', value: locationLabel },
      { label: 'Tarifa referencial', value: sidebarPriceLabel },
      { label: 'Fechas solicitadas', value: requestedRangeLabel },
      { label: 'Contacto', value: reservationForm.contactName || 'Por definir' },
    ];

    if (reservationSuccess) {
      const receiptSnapshot =
        reservationReceipt ||
        {
          pickupLocation: reservationForm.pickupLocation || locationLabel || 'Por definir',
          dropoffLocation: reservationForm.dropoffLocation || 'Por definir',
          requestedFrom: reservationForm.requestedFrom || '',
          requestedTo: reservationForm.requestedTo || '',
          serviceLabel: getPublicationTypeLabel(publicationType),
        };
      const routeLabel = `${receiptSnapshot.pickupLocation || 'Origen pendiente'} → ${
        receiptSnapshot.dropoffLocation || 'Destino pendiente'
      }`;
      const dateLabel = formatDisplayDate(receiptSnapshot.requestedFrom);
      const serviceLabel = receiptSnapshot.serviceLabel || getPublicationTypeLabel(publicationType);

      return (
        <div className="reservation-success-shell">
          <div className="reservation-success-card">
            <div className="reservation-success-card__icon" aria-hidden="true">
              <FaCheckCircle />
            </div>
            <h1>¡Solicitud enviada con éxito!</h1>
            <p>
              Hemos notificado a <strong>{companyName}</strong>. Recibirás una respuesta en menos de
              24 horas.
            </p>

            <div className="reservation-success-receipt">
              <div className="reservation-success-receipt__row">
                <div className="reservation-success-receipt__icon">
                  <FaMapMarkerAlt aria-hidden="true" />
                </div>
                <div>
                  <p className="reservation-success-receipt__label">Ruta solicitada</p>
                  <p className="reservation-success-receipt__value">{routeLabel}</p>
                </div>
              </div>
              <div className="reservation-success-receipt__meta">
                <span>
                  <CalendarDays size={16} aria-hidden="true" /> {dateLabel}
                </span>
                <span className="reservation-success-receipt__pill">{serviceLabel}</span>
              </div>
            </div>

            <div className="reservation-success-actions">
              <button type="button" className="btn primary" onClick={handleGoToReservations}>
                Ir a mis reservas
              </button>
              <button type="button" className="btn ghost" onClick={handleGoHome}>
                Volver al inicio
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="publication-reservation-page reservation-request">
        <div className="publication-reservation-page__inner reservation-request__container">
          <header className="reservation-request__header" role="banner">
            <div>
              <p className="reservation-request__eyebrow">Solicitud al proveedor</p>
              <h1 id="reservation-form-title">Finalizar Solicitud de Servicio</h1>
              <p className="reservation-request__subtitle">
                Complete los detalles operativos para enviar la orden al proveedor.
              </p>
            </div>
            <div className="reservation-request__header-meta">
              <div className="reservation-request__provider">
                <span>Proveedor</span>
                <strong>{companyName}</strong>
              </div>
              <button type="button" className="reservation-request__back" onClick={handleReservationClose}>
                <FaArrowLeft aria-hidden="true" /> Volver a la publicación
              </button>
            </div>
          </header>

          <div className="reservation-request__layout">
            <section
              id="publication-reservation"
              className={`publication-card publication-reservation-card reservation-form-shell${
                reservationSuccess ? ' publication-reservation-card--success reservation-form-shell--success' : ''
              }`}
              tabIndex={0}
              role="group"
              aria-labelledby="reservation-form-title"
            >
              <div className="publication-reservation-card__body">
              {reservationBlockedReason ? (
                <div className="publication-reservation-card__alert" role="status">
                  <FaTimesCircle aria-hidden="true" />
                  <span>{reservationBlockedReason}</span>
                </div>
              ) : (
                <form
                  id={reservationFormId}
                  className="publication-reservation-card__form"
                  onSubmit={handleReservationSubmit}
                >
                    {reservationError ? (
                      <div className="publication-reservation-card__error" role="alert">
                        <FaTimesCircle aria-hidden="true" />
                        <div>
                          <span>{reservationError}</span>
                          {reservationFieldErrors.length ? (
                            <ul className="publication-reservation-card__error-details">
                              {reservationFieldErrors.map(item => (
                                <li key={`${item.field}-${item.message}`}>
                                  <strong>{item.label}</strong>: {item.message}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    <fieldset className="publication-reservation-card__section">
                      <legend>
                        <CalendarDays size={16} aria-hidden="true" />
                        <span>Agenda y ruta</span>
                      </legend>
                      {reservationDateConflict ? (
                        <div
                          className="publication-reservation-card__error publication-reservation-card__error--inline"
                          role="alert"
                        >
                          <FaTimesCircle aria-hidden="true" />
                          <span>{reservationDateConflict.message}</span>
                        </div>
                      ) : null}
                      <div className="publication-reservation-card__grid">
                        <label className="publication-reservation-card__field">
                          <span>Fecha de inicio *</span>
                          <input
                            type="date"
                            name="requestedFrom"
                            value={reservationForm.requestedFrom}
                            onChange={handleReservationFieldChange}
                            required
                            disabled={reservationSubmitting}
                          />
                        </label>
                        <label className="publication-reservation-card__field">
                          <span>Fecha de término</span>
                          <input
                            type="date"
                            name="requestedTo"
                            value={reservationForm.requestedTo}
                            onChange={handleReservationFieldChange}
                            disabled={reservationSubmitting}
                          />
                        </label>
                        <label className="publication-reservation-card__field">
                          <span>Punto de recolección *</span>
                          <input
                            type="text"
                            name="pickupLocation"
                            value={reservationForm.pickupLocation}
                            onChange={handleReservationFieldChange}
                            placeholder="Ciudad, Estado"
                            required
                            disabled={reservationSubmitting}
                          />
                        </label>
                        <label className="publication-reservation-card__field">
                          <span>Destino *</span>
                          <input
                            type="text"
                            name="dropoffLocation"
                            value={reservationForm.dropoffLocation}
                            onChange={handleReservationFieldChange}
                            placeholder="Ciudad, Estado"
                            required
                            disabled={reservationSubmitting}
                          />
                        </label>
                      </div>
                    </fieldset>

                    <fieldset className="publication-reservation-card__section">
                      <legend>
                        <Package size={16} aria-hidden="true" />
                        <span>Detalles de la carga</span>
                      </legend>
                      <div className="publication-reservation-card__grid publication-reservation-card__grid--stack">
                        <label className="publication-reservation-card__field publication-reservation-card__field--full">
                          <span>Descripción de la mercancía *</span>
                          <textarea
                            name="cargoDescription"
                            value={reservationForm.cargoDescription}
                            onChange={handleReservationFieldChange}
                            placeholder="Ej. 18 pallets con mercancía perecedera, requiere refrigeración a 4°C."
                            required
                            disabled={reservationSubmitting}
                            rows={3}
                          />
                        </label>

                        <label className="publication-reservation-card__field publication-reservation-card__field--full">
                          <span>Requerimientos especiales</span>
                          <textarea
                            name="specialRequirements"
                            value={reservationForm.specialRequirements}
                            onChange={handleReservationFieldChange}
                            placeholder="Indica consideraciones específicas como horario de carga, maniobras, escoltas, etc."
                            disabled={reservationSubmitting}
                            rows={3}
                          />
                        </label>
                      </div>
                    </fieldset>

                    <fieldset className="publication-reservation-card__section">
                      <legend>
                        <UserRound size={16} aria-hidden="true" />
                        <span>Contacto</span>
                      </legend>
                      <div className="publication-reservation-card__grid">
                        <label className="publication-reservation-card__field">
                          <span>Contacto *</span>
                          <input
                            type="text"
                            name="contactName"
                            value={reservationForm.contactName}
                            onChange={handleReservationFieldChange}
                            placeholder="Nombre y apellidos"
                            required
                            disabled={reservationSubmitting}
                          />
                        </label>
                        <label className="publication-reservation-card__field">
                          <span>Correo electrónico *</span>
                          <input
                            type="email"
                            name="contactEmail"
                            value={reservationForm.contactEmail}
                            onChange={handleReservationFieldChange}
                            placeholder="correo@empresa.com"
                            required
                            disabled={reservationSubmitting}
                          />
                        </label>
                        <label className="publication-reservation-card__field">
                          <span>Teléfono *</span>
                          <input
                            type="tel"
                            name="contactPhone"
                            value={reservationForm.contactPhone}
                            onChange={handleReservationFieldChange}
                            placeholder="+52 55 0000 0000"
                            required
                            disabled={reservationSubmitting}
                          />
                        </label>
                        <label className="publication-reservation-card__field publication-reservation-card__field--full">
                          <span>Notas adicionales</span>
                          <textarea
                            name="notes"
                            value={reservationForm.notes}
                            onChange={handleReservationFieldChange}
                            placeholder="Comparte horarios de entrega, referencias internas o datos de operación."
                            disabled={reservationSubmitting}
                            rows={2}
                          />
                        </label>
                      </div>
                    </fieldset>
                  </form>
                )}
              </div>
            </section>

            {!reservationSuccess ? (
              <aside className="reservation-request__aside">
                <div className="reservation-request__aside-card">
                  <div>
                    <p className="reservation-request__aside-label">Resumen de la orden</p>
                    <h3>Verifique antes de enviar</h3>
                  </div>
                  <dl className="reservation-request__aside-list">
                    {reservationSummaryItems.map(item => (
                      <div key={item.label}>
                        <dt>{item.label}</dt>
                        <dd>{item.value || 'Por definir'}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="reservation-request__aside-actions">
                    <button
                      type="submit"
                      form={reservationFormId}
                      className="reservation-request__submit"
                      disabled={reservationSubmitting || Boolean(reservationDateConflict)}
                    >
                      {reservationSubmitting ? 'Enviando solicitud...' : 'Enviar solicitud'}
                    </button>
                    <button
                      type="button"
                      className="reservation-request__cancel"
                      onClick={handleReservationClose}
                      disabled={reservationSubmitting}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </aside>
            ) : null}
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="publication-detail">
      <div className="publication-detail__inner">
        <div className="publication-layout publication-layout--marketplace">
          <main className="publication-main">
            {shouldRenderHero ? (
              <section className="publication-gallery" aria-labelledby={heroTitleId}>
                <div
                  className="publication-gallery__media"
                  role="img"
                  aria-label={`Imagen principal de ${publication.title || 'la unidad'}`}
                  style={heroMediaStyle}
                />
              </section>
            ) : null}

            <section className="publication-panel publication-overview" aria-labelledby={heroTitleId}>
              <div className="publication-overview__heading">
                <h1 id={heroTitleId}>{publication.title || 'Publicacion sin titulo'}</h1>
                <div className="publication-overview__meta">
                  <p className="publication-overview__location">
                    <FaMapMarkerAlt aria-hidden="true" /> {locationLabel}
                  </p>
                  {isVerifiedCompany ? (
                    <span className="publication-verified-chip">
                      <ShieldCheck aria-hidden="true" size={16} strokeWidth={1.6} />
                      Proveedor verificado
                    </span>
                  ) : null}
                </div>
              </div>
              {specItems.length ? (
                <div className="publication-feature-grid" role="list">
                  {specItems.map(renderSpecItem)}
                </div>
              ) : null}
              {publication.description ? (
                <div className="publication-description">
                  <div
                    className="publication-rich-text"
                    dangerouslySetInnerHTML={{ __html: ensureRichText(publication.description) }}
                  />
                </div>
              ) : null}
            </section>

            {shouldShowDestinationPricing ? (
              <section
                className="publication-panel publication-tariffs"
                aria-labelledby="publication-destinations-title"
              >
                <h3 id="publication-destinations-title">Tarifas y Destinos</h3>
                <p className="publication-tariffs__hint">
                  {hasMultipleDestinations
                    ? 'Destinos prioritarios definidos por el proveedor. Ajusta la tarifa segun tu ruta.'
                    : 'Tarifa indicativa compartida por el proveedor para esta unidad.'}
                </p>
                <div className="publication-tariffs__list publication-tariffs__list--simple">
                  {destinationSectionEntries.map(entry => (
                    <div key={entry.id} className="publication-tariffs__row">
                      <div className="publication-tariffs__route">
                        <span className="publication-tariffs__pin" aria-hidden="true">
                          <FaMapMarkerAlt />
                        </span>
                        <div>
                          <strong>{entry.label}</strong>
                          <span>{entry.destinationCode || '—'}</span>
                        </div>
                      </div>
                      <span
                        className={`publication-tariffs__price${
                          entry.hasQuotedPrice ? '' : ' publication-tariffs__price--pending'
                        }`}
                      >
                        {entry.priceLabel}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="publication-panel publication-info-card">
              <header className="publication-info-card__header">
                <span className="icon">
                  <FaClipboardList aria-hidden="true" />
                </span>
                <div>
                  <p className="publication-section-label">SLA · Acuerdo de servicio</p>
                  <h2>Acuerdo de nivel de servicio (SLA)</h2>
                  <p>Conoce los tiempos de respuesta y los compromisos operativos del proveedor.</p>
                </div>
              </header>
              <ul className="publication-faq">
                <li>
                  <button
                    type="button"
                    onClick={() => setIsSlaExpanded(prev => !prev)}
                    aria-expanded={isSlaExpanded}
                  >
                    <span>Compromisos operativos</span>
                    <span className="faq-toggle" aria-hidden="true">
                      {isSlaExpanded ? '-' : '+'}
                    </span>
                  </button>
                  {isSlaExpanded ? (
                    <div className="faq-answer">
                      <p>
                        {hasSlaContent
                          ? publication.sla
                          : 'El proveedor aún no compartió su SLA. Solicita los tiempos de respuesta al contactar.'}
                      </p>
                    </div>
                  ) : null}
                </li>
              </ul>
            </section>

            <ReviewsShowcase
              containerClassName="publication-panel publication-info-card publication-reviews-card"
              title="Lo que opinan los clientes"
              subtitle="Experiencias registradas después de completar un servicio en Flotte"
              icon={<FaStar aria-hidden="true" />}
              reviews={companyReviews}
              loading={companyReviewsLoading}
              emptyMessage="Aún no hay reseñas registradas para este proveedor."
              maxVisible={3}
            />

            {hasPolicies ? (
              <section className="publication-panel publication-info-card">
                <header className="publication-info-card__header">
                  <span className="icon">
                    <FaTag aria-hidden="true" />
                  </span>
                  <div>
                    <p className="publication-section-label">Politicas y SLA</p>
                    <h2>Condiciones del servicio</h2>
                    <p>Lineamientos generales compartidos por el proveedor.</p>
                  </div>
                </header>
                <article>
                  <h3>Politicas</h3>
                  <p>{publication.policies}</p>
                </article>
              </section>
            ) : null}

            {hasFaqs ? (
              <section className="publication-panel publication-info-card">
                <header className="publication-info-card__header">
                  <span className="icon">
                    <FaQuestionCircle aria-hidden="true" />
                  </span>
                  <div>
                    <p className="publication-section-label">FAQ</p>
                    <h2>Preguntas frecuentes</h2>
                    <p>Resuelve dudas rapidas antes de solicitar informacion.</p>
                  </div>
                </header>
                <ul className="publication-faq">
                  {publication.faqs.map((faq, index) => {
                    const isOpen = openFaq === index;
                    return (
                      <li key={`${faq.question}-${index}`}>
                        <button
                          type="button"
                          onClick={() => setOpenFaq(isOpen ? null : index)}
                          aria-expanded={isOpen}
                        >
                          <span>{faq.question || `Pregunta ${index + 1}`}</span>
                          <span className="faq-toggle" aria-hidden="true">
                            {isOpen ? '-' : '+'}
                          </span>
                        </button>
                        {isOpen ? (
                          <div className="faq-answer">
                            <p>{faq.answer || 'Respuesta en construccion.'}</p>
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}

          </main>

          <aside className="publication-aside">
            <div className="publication-aside__sticky">
              {showCompanyCard ? (
                <section className="publication-company-mini" aria-labelledby="publication-company-title">
                  <div className="publication-company-mini__header">
                    <div className="publication-company-mini__logo">
                      {companyLogoUrl ? (
                        <img src={companyLogoUrl} alt={companyName} />
                      ) : (
                        <span>{companyInitial}</span>
                      )}
                    </div>
                    <div className="publication-company-mini__meta">
                      <div className="publication-company-mini__name-row">
                        <h3 id="publication-company-title">{companyName}</h3>
                        {isVerifiedCompany ? (
                          <span className="publication-company-mini__verified">
                            <ShieldCheck aria-hidden="true" size={14} strokeWidth={1.6} />
                            Verificado
                          </span>
                        ) : null}
                      </div>
                      <p className="publication-company-mini__location">
                        <FaMapMarkerAlt aria-hidden="true" /> {locationLabel}
                      </p>
                    </div>
                  </div>
                  <p className="publication-company-mini__copy">
                    Conoce la flota, certificaciones y reputacion del proveedor antes de reservar.
                  </p>
                  <Link className="publication-company-mini__cta" to={companyProfileHref}>
                    Contactar anfitrion
                    <FaArrowRight aria-hidden="true" />
                  </Link>
                </section>
              ) : null}

              <section
                className="publication-booking-card"
                aria-labelledby="publication-booking-title"
              >
                <header className="publication-booking-card__header">
                  <div className="publication-booking-card__headline">
                    <div>
                      <span className="publication-booking-card__price">{sidebarPriceLabel}</span>
                      <small>Tarifa referencial</small>
                    </div>
                    <span className={`status-chip ${unitStatusClassName}`}>{unitStatusLabel}</span>
                  </div>
                  <p className="publication-booking-card__eyebrow">Estado actualizado de la unidad</p>
                </header>
                <div className="publication-booking-card__dates">
                  <span className="publication-booking-card__dates-label">Agenda estimada</span>
                  {isServiceOffer ? (
                    <>
                      <button
                        type="button"
                        className={`publication-booking-card__date-trigger${showCalendar ? ' is-open' : ''}`}
                        onClick={handleToggleCalendar}
                        ref={dateTriggerRef}
                      >
                        <div className="publication-booking-card__date-trigger-header">
                          <CalendarDays aria-hidden="true" size={18} strokeWidth={1.8} />
                          <span>{showCalendar ? 'Cerrar calendario' : 'Seleccionar fechas'}</span>
                        </div>
                        <div className="publication-booking-card__date-slots">
                          <div className="publication-booking-card__date-slot">
                            <span>Inicio</span>
                            <strong>{dateTriggerStartLabel}</strong>
                          </div>
                          <div className="publication-booking-card__date-slot">
                            <span>Fin</span>
                            <strong>{dateTriggerEndLabel}</strong>
                          </div>
                        </div>
                      </button>
                      {showCalendar ? (
                        <div className="publication-booking-card__calendar-popover" ref={calendarPopoverRef}>
                          <DatePickerCalendar
                            value={calendarValue || undefined}
                            readOnly
                            format={AVAILABILITY_FORMAT}
                            numberOfMonths={calendarMonths}
                            weekStartDayIndex={1}
                            className="publication-calendar__widget"
                            mapDays={({ date }) => {
                              const current = date.toDate();
                              const normalizedCurrent = new Date(
                                current.getFullYear(),
                                current.getMonth(),
                                current.getDate()
                              );
                              const key = formatDateKey(normalizedCurrent);
                              const isUnitDay = availabilityDates.has(key);
                              const isPublicationDay =
                                !showUnitAvailability &&
                                hasPublicationRange &&
                                key >= publicationRangeStartKey &&
                                key <= publicationRangeEndKey;

                              const classes = [];
                              const style = {};
                              let isAvailable = false;

                              const setHighlight = (options = {}) => {
                                Object.assign(style, options);
                              };

                              if (isUnitDay && showUnitAvailability) {
                                const isStart = availabilityRangeStarts.has(key);
                                const isEnd = availabilityRangeEnds.has(key);
                                isAvailable = true;
                                if (isStart && isEnd) {
                                  classes.push('publication-calendar__day--single');
                                  setHighlight({
                                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                    color: '#f8fafc',
                                    borderRadius: '999px',
                                  });
                                } else if (isStart) {
                                  classes.push('publication-calendar__day--start');
                                  setHighlight({
                                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                    color: '#f8fafc',
                                    borderRadius: '999px 10px 10px 999px',
                                  });
                                } else if (isEnd) {
                                  classes.push('publication-calendar__day--end');
                                  setHighlight({
                                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                    color: '#f8fafc',
                                    borderRadius: '10px 999px 999px 10px',
                                  });
                                } else {
                                  classes.push('publication-calendar__day--between');
                                  setHighlight({
                                    background: 'linear-gradient(135deg, rgba(134, 239, 172, 0.45), rgba(74, 222, 128, 0.28))',
                                    color: '#166534',
                                    borderRadius: '12px',
                                  });
                                }
                              } else if (isPublicationDay) {
                                const isStart = key === publicationRangeStartKey;
                                const isEnd = key === publicationRangeEndKey;
                                isAvailable = true;
                                if (isStart && isEnd) {
                                  classes.push('publication-calendar__day--single');
                                  setHighlight({
                                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                    color: '#f8fafc',
                                    borderRadius: '999px',
                                  });
                                } else if (isStart) {
                                  classes.push('publication-calendar__day--start');
                                  setHighlight({
                                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                    color: '#f8fafc',
                                    borderRadius: '999px 10px 10px 999px',
                                  });
                                } else if (isEnd) {
                                  classes.push('publication-calendar__day--end');
                                  setHighlight({
                                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                    color: '#f8fafc',
                                    borderRadius: '10px 999px 999px 10px',
                                  });
                                } else {
                                  classes.push('publication-calendar__day--between');
                                  setHighlight({
                                    background: 'linear-gradient(135deg, rgba(134, 239, 172, 0.45), rgba(74, 222, 128, 0.28))',
                                    color: '#166534',
                                    borderRadius: '12px',
                                  });
                                }
                              } else if (assumeAlwaysAvailable) {
                                isAvailable = true;
                                classes.push('publication-calendar__day--always');
                                setHighlight({
                                  background: 'linear-gradient(135deg, rgba(134, 239, 172, 0.45), rgba(74, 222, 128, 0.28))',
                                  color: '#166534',
                                  borderRadius: '12px',
                                });
                              }

                              if (!isAvailable) {
                                classes.push('publication-calendar__day--unavailable');
                                setHighlight({
                                  background: 'linear-gradient(135deg, rgba(248, 113, 113, 0.9), rgba(239, 68, 68, 0.85))',
                                  color: '#ffffff',
                                  borderRadius: '12px',
                                });
                              }

                              if (normalizedCurrent < today) {
                                classes.push('publication-calendar__day--past');
                                setHighlight({
                                  background: 'linear-gradient(140deg, #e2e8f0, #cbd5f5)',
                                  color: '#475569',
                                  borderRadius: style.borderRadius || '12px',
                                });
                              }

                              if (!classes.length) {
                                return {};
                              }

                              return { className: classes.join(' '), style };
                            }}
                          />
                          <div className="publication-calendar__legend">
                            <span className="publication-calendar__legend-item">
                              <span className="publication-calendar__legend-dot publication-calendar__legend-dot--available" />
                              <span>Disponible</span>
                            </span>
                            <span className="publication-calendar__legend-item">
                              <span className="publication-calendar__legend-dot publication-calendar__legend-dot--unavailable" />
                              <span>No disponible</span>
                            </span>
                          </div>
                        </div>
                      ) : null}
                      <p className="publication-booking-card__note">{calendarFooterText}</p>
                    </>
                  ) : (
                    <p className="publication-booking-card__note">{calendarFooterText}</p>
                  )}
                </div>
                <div className="publication-booking-card__actions">
                  <button
                    type="button"
                    className="btn primary"
                    onClick={handleReservationNavigate}
                    disabled={!canRequestReservation}
                  >
                    Solicitar cotizacion
                  </button>
                  {reservationBlockedReason ? (
                    <div className="publication-booking-card__status-strip" role="status">
                      <FaTimesCircle aria-hidden="true" />
                      <span>{reservationBlockedReason}</span>
                    </div>
                  ) : (
                    <small>Sin cargos hasta confirmar con el proveedor.</small>
                  )}
                </div>
              </section>
            </div>
          </aside>
        </div>
        {shouldShowSimilarSection ? (
          <section className="publication-related-shell" aria-labelledby="publication-related-title">
            <div className="publication-related-divider" aria-hidden="true" />
            <header className="publication-related-shell__header publication-related-shell__header--inline">
              <h2 id="publication-related-title">Otras opciones en la zona</h2>
              <p>Publicaciones activas en {relatedLocationLabel} con unidades similares.</p>
            </header>
            {similarLoading ? (
              <div className="publication-related__grid" role="region" aria-live="polite">
                {Array.from({ length: 3 }).map((_, index) => (
                  <article
                    key={`publication-related-loading-${index}`}
                    className="publication-related__item publication-related__item--loading"
                  >
                    <div className="publication-related__media publication-related__media--loading">
                      <span className="skeleton publication-related__skeleton-media" />
                    </div>
                    <div className="publication-related__body publication-related__body--skeleton">
                      <span className="skeleton publication-related__skeleton-line publication-related__skeleton-line--title" />
                      <span className="skeleton publication-related__skeleton-line" />
                      <span className="skeleton publication-related__skeleton-line publication-related__skeleton-line--short" />
                      <div className="publication-related__skeleton-actions">
                        <span className="skeleton publication-related__skeleton-button" />
                        <span className="skeleton publication-related__skeleton-button" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : similarPublications.length ? (
              (() => {
                const similarItems = similarPublications.slice(0, MAX_SIMILAR_VISIBLE);
                const fillerCount = Math.max(0, MIN_SIMILAR_COLUMNS - similarItems.length);
                const fillerItems = Array.from({ length: fillerCount }).map((_, fillerIndex) => ({
                  id: `publication-related-placeholder-${fillerIndex}`,
                  placeholder: true,
                }));
                const displayItems = [...similarItems, ...fillerItems];

                return (
                  <div className="publication-related__grid" role="region" aria-live="polite">
                    {displayItems.map((item, index) => {
                      if (item && !item.placeholder) {
                        const targetIdentifier = item?.numericId || item?.id || item?.slug;
                        const itemKey = targetIdentifier || `publication-related-${index}`;
                        const location = item?.location || 'Ubicacion por confirmar';
                        const imageCandidates = [
                          item?.bannerUrl,
                          item?.primaryImageUrl,
                          item?.imageUrl,
                          item?.thumbnailUrl,
                          item?.coverUrl,
                          item?.photoUrl,
                          item?.heroUrl,
                          item?.heroImage,
                          item?.hero,
                          item?.companyHeroUrl,
                          item?.companyHero,
                          item?.companyCoverUrl,
                          item?.unitPhotoUrl,
                          Array.isArray(item?.photos) ? item.photos[0] : null,
                          Array.isArray(item?.images) ? item.images[0] : null,
                          Array.isArray(item?.media) ? item.media[0] : null,
                        ];
                        const coverUrl =
                          imageCandidates
                            .map(candidate => {
                              if (!candidate) return '';
                              if (typeof candidate === 'string') return candidate;
                              if (typeof candidate === 'object') {
                                return (
                                  candidate.url ||
                                  candidate.src ||
                                  candidate.href ||
                                  (typeof candidate.path === 'string' ? candidate.path : '')
                                );
                              }
                              return '';
                            })
                            .find(value => typeof value === 'string' && value.trim()) ||
                          FALLBACK_SIMILAR_IMAGE;
                        const isFallbackImage = coverUrl === FALLBACK_SIMILAR_IMAGE;
                        const title = item?.title || item?.name || 'Publicacion destacada';
                        const rawSummary =
                          item?.summary ||
                          item?.description ||
                          'Conoce disponibilidad, precios y certificaciones directamente con este proveedor.';
                        const summary = stripHtml(rawSummary).trim();
                        const companyLabel =
                          [item?.companyName, item?.company, item?.vendorName]
                            .map(value => (typeof value === 'string' ? value.trim() : ''))
                            .find(Boolean) || 'Proveedor';
                        const priceLabel = item?.priceLabel || item?.price || '';
                        const updatedLabel = item?.updatedLabel || item?.updatedAgo || '';
                        const unitMeta = [
                          item?.unitTypeLabel || item?.unitType,
                          item?.year || item?.modelYear,
                        ]
                          .map(value => (typeof value === 'string' ? value.trim() : ''))
                          .filter(Boolean)
                          .join(' • ');
                        const isMessySummary =
                          !summary ||
                          summary.length < 20 ||
                          summary === summary.toUpperCase();
                        const displaySummary =
                          !isMessySummary ? summary : unitMeta || 'Consulta disponibilidad y especificaciones con el proveedor.';
                        const ratingValue = Number(item?.rating || item?.score || item?.ratingValue);
                        const normalizedRating =
                          Number.isFinite(ratingValue) && ratingValue > 0
                            ? Math.max(1, Math.min(5, Math.round(ratingValue)))
                            : 0;
                        const ratingLabelValue =
                          Number.isFinite(ratingValue) && ratingValue > 0 ? ratingValue : normalizedRating;
                        const detailHref = targetIdentifier ? `/publicaciones/${targetIdentifier}` : '';
                        const reserveHref = targetIdentifier ? `/publicaciones/${targetIdentifier}/reservar` : '';

                        return (
                          <article key={itemKey} className="publication-related__item">
                            <div className="publication-related__media">
                              <img
                                src={coverUrl}
                                alt={title}
                                loading="lazy"
                                className={isFallbackImage ? 'publication-related__media-image--fallback' : undefined}
                              />
                              <div className="publication-related__badges">
                                {location ? (
                                  <span className="publication-related__badge">
                                    <FaMapMarkerAlt aria-hidden="true" /> {location}
                                  </span>
                                ) : null}
                                {companyLabel ? (
                                  <span className="publication-related__badge publication-related__badge--muted">
                                    {companyLabel}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <div className="publication-related__body">
                              <div className="publication-related__heading">
                                <h3 title={title}>{title}</h3>
                                {updatedLabel ? <span className="publication-related__time">{updatedLabel}</span> : null}
                              </div>
                              {displaySummary ? (
                                <p className="publication-related__summary">{displaySummary}</p>
                              ) : null}
                              {(priceLabel || normalizedRating > 0) ? (
                                <div className="publication-related__footer">
                                  {priceLabel ? (
                                    <span className="publication-related__price">{priceLabel}</span>
                                  ) : null}
                                  {normalizedRating > 0 ? (
                                    <div
                                      className="publication-related__rating"
                                      aria-label={`Calificacion ${ratingLabelValue} de 5`}
                                    >
                                      {Array.from({ length: 5 }).map((_, starIndex) => (
                                        <FaStar
                                          key={starIndex}
                                          className={starIndex < normalizedRating ? 'is-active' : undefined}
                                          aria-hidden="true"
                                        />
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}
                              {detailHref ? (
                                <div className="publication-related__actions">
                                  <Link
                                    className="publication-related__button publication-related__button--ghost"
                                    to={detailHref}
                                  >
                                    Ver
                                  </Link>
                                  {reserveHref ? (
                                    <Link
                                      className="publication-related__button publication-related__button--primary"
                                      to={reserveHref}
                                    >
                                      Reservar
                                    </Link>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </article>
                        );
                      }

                      return (
                        <article
                          key={item?.id || `publication-related-placeholder-${index}`}
                          className="publication-related__item publication-related__item--placeholder"
                          aria-hidden="true"
                        />
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <p className="publication-related__message">
                {similarError || 'No encontramos mas publicaciones con estas caracteristicas por ahora.'}
              </p>
            )}
          </section>
        ) : null}

      </div>
    </div>
  );
}
